<template>
  <div :class="[`overflow-visible relative mx-2  bg-white dark:bg-dark-box-background p-2 rounded-lg mb-4 `]">
    <div class="border-b border-[#B3CAD6] dark:border-dark-divider-border p-3">
      <div class="flex items-center">
        <div
          class="flex items-center ml-2 space-x-4 text-lg cursor-pointer text-grey-light-body dark:text-dark-body-text"
          @click="$router.push(`/workspace/${cloned_workspace.short_id}/${cloned_workspace?.layout.filter((l) => l.is_visible)[0]?.id}/`)">
          <img
            v-if="$utils.extractUrl(cloned_workspace.logo)"
            class="inline-block rounded-full h-9 w-9 object-cover object-contain"
            :src="$utils.extractUrl(cloned_workspace.logo)"
            alt="">
          <Avatar v-else :is-company="false" :first-name="cloned_workspace.name" />
          <div>
            <div>{{ cloned_workspace.name }}</div>
            <div class="text-sm text-common-grey">
              {{ $utils.getRecordName(cloned_workspace.record) }}
            </div>
          </div>
        </div>
        <!-- <HideIcon color="#899399" class="w-4 h-4 ml-3 mr-1 cursor-pointer" /> -->

        <Menu v-if="is_admin_user" as="div" class="flex-shrink-0 ml-auto">
          <MenuButton
            :style="{ backgroundColor: $store.state.ui_options?.theme?.button_color }"
            class="w-8 h-8 p-2 border rounded-md inline-flex items-center justify-center focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
            <span class="sr-only">{{ $t("Open options") }}</span>
            <QuickActionsIcon color="white" class="w-4 h-4" aria-hidden="true" />
          </MenuButton>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0">
            <MenuItems
              class="absolute z-50 w-48 mx-3 mt-1 origin-bottom-right bg-white rounded-md shadow-lg -ml-44 dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none">
              <MenuItem v-slot="{ active }">
                <div
                  :class="[
                    active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                    ' cursor-pointer group flex items-center px-4 py-2 text-sm'
                  ]"
                  @click="$router.push(`/workspace/edit/${cloned_workspace.short_id}`)">
                  <Pencil
                    :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                    class="w-5 h-5 mr-4 transition ease-in-out duration-side-menu-transition-duration" />
                  {{ $t("Edit") }}
                </div>
              </MenuItem>

              <MenuItem v-slot="{ active }">
                <div
                  :class="[
                    active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                    ' cursor-pointer group flex items-center px-4 py-2 text-sm'
                  ]"
                  @click="$emitter.emit(ALL_EMITTERS.DELETE_WORKSPACES, [cloned_workspace.short_id])">
                  <Trash2
                    :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                    class="w-5 h-5 mr-4 transition ease-in-out dark:text-white duration-side-menu-transition-duration" />
                  {{ $t("Delete") }}
                </div>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>

    <div class="p-2 mt-3">
      <div class="flex mb-2">
        <div class="text-common-grey">
          {{ $t("Team collaborators") }}
        </div>
        <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
          {{ cloned_workspace.team_members.length }}
        </div>
      </div>
      <div class="flex mb-2">
        <div class="text-common-grey">
          {{ $t("Contact collaborators") }}
        </div>
        <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
          {{ cloned_workspace.contacts.length }}
        </div>
      </div>

      <div class="flex mb-2">
        <div class="text-common-grey">
          {{ $t("Last opened") }}
        </div>
        <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
          {{ getLastOpened(cloned_workspace) }}
        </div>
      </div>

      <div class="flex mb-2">
        <div class="text-common-grey">
          {{ $t("Last modified") }}
        </div>
        <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
          {{ getLastModified(cloned_workspace) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/shared/Avatar.vue"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"

import QuickActionsIcon from "@/components/shared/icons/QuickActionsIcon.vue"
import { Trash2, Pencil } from "lucide-vue-next"

import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Avatar,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    QuickActionsIcon,
    Pencil,
    Trash2
  },
  props: {
    workspace: null
  },
  emits: [],
  data() {
    return {
      ALL_EMITTERS,
      cloned_workspace: null
    }
  },
  computed: {
    is_admin_user() {
      const collaborator = [...this.cloned_workspace.team_members, ...this.cloned_workspace.contacts].find(
        (c) => c.id == this.$store.state.auth.user.id
      )
      return collaborator?.workspace_access?.access_scope == "admin"
    }
  },
  watch: {},

  created() {
    this.cloned_workspace = this.workspace
    this.register_listeners()
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.CONFIRM_DELETE_WORKSPACES, (workspaces) => {
        this.$store.dispatch("data/deleteWorkspace", workspaces[0])
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.CONFIRM_DELETE_WORKSPACES)
    },
    getLastOpened(workspace) {
      let last_opened_calc = null
      workspace.contacts.concat(workspace.team_members).forEach((c) => {
        if (c.workspace_access.last_workspace_access) {
          if (c.workspace_access.last_workspace_access && !last_opened_calc) {
            last_opened_calc = c.workspace_access.last_workspace_access
          } else {
            if (c.workspace_access.last_workspace_access && last_opened_calc < c.workspace_access.last_workspace_access) {
              last_opened_calc = c.workspace_access.last_workspace_access
            }
          }
        }
      })

      if (last_opened_calc) {
        return this.$moment(last_opened_calc).fromNow()
      }
      return "never"
    },
    getLastModified(workspace) {
      let last_modified_calc = null
      workspace.contacts.concat(workspace.team_members).forEach((c) => {
        if (c.workspace_access.last_workspace_edit) {
          if (c.workspace_access.last_workspace_edit && !last_modified_calc) {
            last_modified_calc = c.workspace_access.last_workspace_edit
          } else {
            if (c.workspace_access.last_workspace_edit && last_modified_calc < c.workspace_access.last_workspace_edit) {
              last_modified_calc = c.workspace_access.last_workspace_edit
            }
          }
        }
      })

      if (last_modified_calc) {
        return this.$moment(last_modified_calc).fromNow()
      }
      return "never"
    }
  }
}
</script>
